import { Component, ReactNode } from 'react';
import { RefreshCw, Home, WifiOff, FileWarning, ServerCrash, ChevronDown, Copy, Check } from 'lucide-react';
import { clearCachesAndReload } from 'src/lib/clearCachesAndReload';
import { logger } from 'src/lib/logger';
import { faro } from '@grafana/faro-web-sdk';
import { Button } from './ui/Button';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  showDetails: boolean;
  copied: boolean;
}

type ErrorType = 'network' | 'chunk' | 'unknown';

interface ErrorConfig {
  icon: ReactNode;
  title: string;
  description: string;
  suggestion: string;
}

const errorConfigs: Record<ErrorType, ErrorConfig> = {
  network: {
    icon: <WifiOff className="h-16 w-16" />,
    title: 'Connection Lost',
    description: "We couldn't reach the server. Please check your internet connection.",
    suggestion: 'Try reconnecting to your network or refreshing the page.',
  },
  chunk: {
    icon: <FileWarning className="h-16 w-16" />,
    title: 'Page Load Failed',
    description: 'Some resources failed to load. This can happen after a deployment.',
    suggestion: 'Please reload the page.',
  },
  unknown: {
    icon: <ServerCrash className="h-16 w-16" />,
    title: 'Something Went Wrong',
    description: 'An unexpected error occurred while loading this page.',
    suggestion: 'Try refreshing the page or go back to the home page.',
  },
};

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, showDetails: true, copied: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const context = {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };

    logger.error('[ErrorBoundary] Error caught:', { error, errorInfo });
    logger.captureError(error, context);

    faro.api?.pushError(error, {
      context: {
        componentStack: errorInfo.componentStack || '',
        source: 'ErrorBoundary',
      },
    });
  }

  getErrorType(): ErrorType {
    const message = this.state.error?.message;
    if (
      message?.includes('Failed to fetch dynamically imported module') ||
      message?.includes('Loading chunk') ||
      message?.includes('Unable to preload CSS')
    ) {
      return 'chunk';
    }
    if (
      message?.includes('NetworkError') ||
      message?.includes('Failed to fetch') ||
      message?.includes('Network request failed')
    ) {
      return 'network';
    }
    return 'unknown';
  }

  copyErrorDetails = async () => {
    const errorDetails = this.state.error?.stack || this.state.error?.message || '';
    await navigator.clipboard.writeText(errorDetails);
    this.setState({ copied: true });
    setTimeout(() => this.setState({ copied: false }), 2000);
  };

  render() {
    if (this.state.hasError) {
      const errorType = this.getErrorType();
      const config = errorConfigs[errorType];
      const errorDetails = this.state.error?.stack || this.state.error?.message;

      if (errorType === 'chunk') {
        return (
          <>
            {this.props.children}
            <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-lg mx-auto px-4">
              <div className="rounded-lg border bg-card p-4 shadow-lg space-y-2">
                <div className="flex items-center gap-3">
                  <FileWarning className="h-5 w-5 shrink-0 text-muted-foreground" />
                  <p className="text-sm text-foreground flex-1">Failed to load page. Try reloading.</p>
                  <Button size="sm" onClick={clearCachesAndReload} className="gap-1.5 shrink-0">
                    <RefreshCw className="h-3.5 w-3.5" />
                    Reload
                  </Button>
                </div>
                <pre className="text-xs text-muted-foreground bg-muted/50 rounded p-2 overflow-auto max-h-24 whitespace-pre-wrap break-all">
                  {this.state.error?.message}
                </pre>
              </div>
            </div>
          </>
        );
      }

      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-background p-6">
          <div className="w-full max-w-md space-y-8 text-center">
            <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-muted text-muted-foreground">
              {config.icon}
            </div>

            <div className="space-y-3">
              <h1 className="text-3xl font-bold tracking-tight text-foreground">{config.title}</h1>
              <p className="text-base text-muted-foreground">{config.description}</p>
              <p className="text-sm text-muted-foreground/80">{config.suggestion}</p>
            </div>

            <div className="flex flex-col gap-3 pt-4">
              <Button onClick={() => window.location.reload()} size="lg" className="w-full gap-2">
                <RefreshCw className="h-4 w-4" />
                Reload Page
              </Button>
              <Button variant="outline" onClick={() => (window.location.href = '/')} size="lg" className="w-full gap-2">
                <Home className="h-4 w-4" />
                Go to Home
              </Button>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <div className="pt-4">
                <button
                  onClick={() => this.setState({ showDetails: !this.state.showDetails })}
                  className="flex w-full items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${this.state.showDetails ? 'rotate-180' : ''}`}
                  />
                  {this.state.showDetails ? 'Hide' : 'Show'} Error Details
                </button>
                {this.state.showDetails && (
                  <div className="mt-4 rounded-lg border bg-muted/50 p-4 text-left">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Stack Trace
                      </span>
                      <Button variant="ghost" size="sm" onClick={this.copyErrorDetails} className="h-7 gap-1.5 text-xs">
                        {this.state.copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                        {this.state.copied ? 'Copied' : 'Copy'}
                      </Button>
                    </div>
                    <pre className="overflow-auto text-xs text-muted-foreground max-h-48 whitespace-pre-wrap wrap-break-word">
                      {errorDetails}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
